import { useState, useEffect } from 'react';
import PageHeader from '../components/ui/page-header';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  User,
  Building,
  Mail,
  Shield,
  Calendar,
  LogOut,
  Bell,
  Clock,
  Briefcase,
  Camera,
  Activity,
  Loader2,
  Save,
  Check,
  Smartphone,
  KeyRound,
  AlertTriangle,
  RefreshCw,
  Upload,
  X,
  ImageIcon,
  Info,
  Package
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { resetOnboardingTour } from '../components/OnboardingTour';
import { resetContextualTips } from '../components/ContextualTip';
import { resetAllCoachmarks } from '../components/Coachmark';

// MFA Section Component
const MFASection = ({ api, forceSetup = false }) => {
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false, mfa_method: null });
  const [loading, setLoading] = useState(true);
  const [showSetupDialog, setShowSetupDialog] = useState(forceSetup);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [setupStep, setSetupStep] = useState('method'); // method, verify
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const res = await api.get('/user/mfa/status');
      setMfaStatus(res.data);
    } catch (error) {
      console.error('Error fetching MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async (method) => {
    setProcessing(true);
    try {
      const res = await api.post('/user/mfa/setup', { method });
      setSetupData(res.data);
      setSelectedMethod(method);
      setSetupStep('verify');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De MFA-instelling kon niet worden geactiveerd. Probeer het opnieuw.'));
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length < 6) {
      toast.error('Voer een geldige 6-cijferige code in');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/user/mfa/verify', { code: verifyCode });
      toast.success('MFA succesvol ingeschakeld!');
      setShowSetupDialog(false);
      setSetupStep('method');
      setVerifyCode('');
      setSetupData(null);
      fetchMFAStatus();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De verificatiecode is onjuist. Controleer de code en probeer het opnieuw.'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable = async () => {
    if (!verifyCode || verifyCode.length < 6) {
      toast.error('Voer een geldige code in om MFA uit te schakelen');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/user/mfa/disable', { code: verifyCode });
      toast.success('MFA uitgeschakeld');
      setShowDisableDialog(false);
      setVerifyCode('');
      fetchMFAStatus();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'MFA kon niet worden uitgeschakeld. Probeer het later opnieuw.'));
    } finally {
      setProcessing(false);
    }
  };

  const handleResendCode = async () => {
    setProcessing(true);
    try {
      const res = await api.post('/user/mfa/send-code');
      toast.success(res.data.message);
      if (res.data.debug_code) {
        // Show debug code in development
        toast.info(`Debug code: ${res.data.debug_code}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error('De verificatiecode kon niet worden verstuurd. Probeer het later opnieuw.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-black">Tweestapsverificatie (MFA)</p>
              <p className="text-xs text-neutral-500">
                {mfaStatus.mfa_enabled 
                  ? `Ingeschakeld via ${mfaStatus.mfa_method === 'totp' ? 'Authenticator app' : 'Email'}`
                  : 'Voeg een extra beveiligingslaag toe aan uw account'
                }
              </p>
            </div>
          </div>
          {mfaStatus.mfa_enabled ? (
            <Badge className="bg-emerald-100 text-emerald-700">Actief</Badge>
          ) : (
            <Badge className="bg-neutral-100 text-neutral-600">Niet actief</Badge>
          )}
        </div>

        {mfaStatus.mfa_enabled ? (
          <Button
            variant="outline"
            size="sm"
            className="border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => setShowDisableDialog(true)}
            data-testid="disable-mfa-button"
          >
            MFA Uitschakelen
          </Button>
        ) : (
          <Button
            size="sm"
            className="bg-black text-white hover:bg-neutral-800"
            onClick={() => setShowSetupDialog(true)}
            data-testid="setup-mfa-button"
          >
            <Shield className="h-4 w-4 mr-2" strokeWidth={1.5} />
            MFA Inschakelen
          </Button>
        )}
      </div>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={(open) => {
        setShowSetupDialog(open);
        if (!open) {
          setSetupStep('method');
          setVerifyCode('');
          setSetupData(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {setupStep === 'method' ? 'Kies MFA Methode' : 'Verifieer MFA'}
            </DialogTitle>
            <DialogDescription>
              {setupStep === 'method' 
                ? 'Kies hoe u wilt inloggen met tweestapsverificatie'
                : selectedMethod === 'totp' 
                  ? 'Scan de QR code met uw authenticator app en voer de code in'
                  : 'Voer de code in die naar uw e-mail is verzonden'
              }
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-3 px-1" data-testid="mfa-step-indicator">
            <div className="flex items-center gap-2">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${setupStep === 'method' ? 'bg-black text-white' : 'bg-green-100 text-green-700'}`}>
                {setupStep === 'method' ? '1' : <Check className="h-3.5 w-3.5" />}
              </div>
              <span className="text-xs text-neutral-600">Methode</span>
            </div>
            <div className={`flex-1 h-px ${setupStep === 'verify' ? 'bg-black' : 'bg-neutral-200'}`} />
            <div className="flex items-center gap-2">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${setupStep === 'verify' ? 'bg-black text-white' : 'bg-neutral-200 text-neutral-500'}`}>
                2
              </div>
              <span className="text-xs text-neutral-600">Verifiëren</span>
            </div>
          </div>

          {setupStep === 'method' ? (
            <div className="space-y-4 py-4">
              <button
                className="w-full p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors text-left flex items-center gap-4"
                onClick={() => handleStartSetup('totp')}
                disabled={processing}
                data-testid="mfa-method-totp"
              >
                <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                  <Smartphone className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-black">Authenticator App</p>
                  <p className="text-xs text-neutral-500">Gebruik Google Authenticator, Authy of vergelijkbaar</p>
                </div>
                {processing && selectedMethod === 'totp' && <Loader2 className="h-4 w-4 animate-spin" />}
              </button>

              <button
                className="w-full p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors text-left flex items-center gap-4"
                onClick={() => handleStartSetup('email')}
                disabled={processing}
                data-testid="mfa-method-email"
              >
                <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-black">E-mail Code</p>
                  <p className="text-xs text-neutral-500">Ontvang een code via e-mail bij elke login</p>
                </div>
                {processing && selectedMethod === 'email' && <Loader2 className="h-4 w-4 animate-spin" />}
              </button>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {selectedMethod === 'totp' && setupData?.qr_code && (
                <div className="flex flex-col items-center gap-4">
                  <img 
                    src={setupData.qr_code} 
                    alt="QR Code" 
                    className="w-48 h-48 border border-neutral-200 rounded-lg"
                  />
                  <p className="text-xs text-neutral-500 text-center">
                    Of voer deze code handmatig in: <code className="bg-neutral-100 px-2 py-0.5 rounded">{setupData.secret}</code>
                  </p>
                </div>
              )}

              {selectedMethod === 'email' && (
                <div className="text-center py-4">
                  <Mail className="h-12 w-12 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-sm text-neutral-600">{setupData?.message}</p>
                  {setupData?.debug_code && (
                    <p className="text-xs text-amber-600 mt-2">Debug code: {setupData.debug_code}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="mfa-code">Verificatiecode</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="mfa-verify-code-input"
                />
              </div>

              {selectedMethod === 'email' && (
                <Button
                  variant="link"
                  size="sm"
                  className="w-full"
                  onClick={handleResendCode}
                  disabled={processing}
                >
                  Code opnieuw versturen
                </Button>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {setupStep === 'verify' && (
              <Button 
                variant="outline" 
                onClick={() => { setSetupStep('method'); setVerifyCode(''); setSetupData(null); }}
                data-testid="mfa-back-button"
              >
                Terug
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
              Annuleren
            </Button>
            {setupStep === 'verify' && (
              <Button
                className="bg-black text-white hover:bg-neutral-800"
                onClick={handleVerifySetup}
                disabled={processing || verifyCode.length < 6}
                data-testid="mfa-verify-button"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Verifiëren
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>MFA Uitschakelen</DialogTitle>
            <DialogDescription>
              Voer uw huidige MFA code in om tweestapsverificatie uit te schakelen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable-mfa-code">Verificatiecode</Label>
              <Input
                id="disable-mfa-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
                data-testid="mfa-disable-code-input"
              />
            </div>

            {mfaStatus.mfa_method === 'email' && (
              <Button
                variant="link"
                size="sm"
                className="w-full"
                onClick={handleResendCode}
                disabled={processing}
              >
                Code versturen naar e-mail
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
              Annuleren
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDisable}
              disabled={processing || verifyCode.length < 6}
              data-testid="mfa-disable-confirm-button"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Uitschakelen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SettingsPage = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState([]);
  const [allowAdminAccess, setAllowAdminAccess] = useState(false);
  const [savingAdminAccess, setSavingAdminAccess] = useState(false);
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Check if MFA setup is required (from login redirect)
  const mfaSetupRequired = searchParams.get('setup_mfa') === 'required';
  const urlTab = searchParams.get('tab');
  
  // Profile state
  const [profile, setProfile] = useState({
    full_name: '',
    job_title: '',
    profile_photo_url: ''
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    reminder_frequency: 'monthly',
    email_notifications: true,
    in_app_notifications: true
  });

  useEffect(() => {
    fetchData();
  }, []);
  
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens zijn');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/user/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Wachtwoord succesvol gewijzigd. Een bevestigingsemail is verzonden.');
      setShowPasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Uw wachtwoord kon niet worden gewijzigd. Controleer uw huidige wachtwoord.'));
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchData = async () => {
    try {
      const [profileRes, prefsRes, activitiesRes, adminAccessRes] = await Promise.all([
        api.get('/user/profile'),
        api.get('/user/preferences'),
        api.get('/user/activities'),
        api.get('/user/admin-access').catch(() => ({ data: { allow_admin_access: false } }))
      ]);
      
      setProfile({
        full_name: profileRes.data.full_name || '',
        job_title: profileRes.data.job_title || '',
        profile_photo_url: profileRes.data.profile_photo_url || ''
      });
      
      setPreferences({
        reminder_frequency: prefsRes.data.reminder_frequency || 'monthly',
        email_notifications: prefsRes.data.email_notifications ?? true,
        in_app_notifications: prefsRes.data.in_app_notifications ?? true
      });
      
      setActivities(activitiesRes.data?.activities || []);
      setAllowAdminAccess(adminAccessRes.data?.allow_admin_access || false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Uw instellingen konden niet worden geladen. Ververs de pagina.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdminAccess = async (newValue) => {
    setSavingAdminAccess(true);
    try {
      await api.patch(`/user/admin-access?allow=${newValue}`);
      setAllowAdminAccess(newValue);
      toast.success(newValue ? 'Admin toegang ingeschakeld' : 'Admin toegang uitgeschakeld');
    } catch (error) {
      toast.error('Instelling opslaan mislukt');
    } finally {
      setSavingAdminAccess(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Alleen afbeeldingen zijn toegestaan');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Bestand is te groot (max 2MB)');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/user/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(p => ({ ...p, profile_photo_url: res.data.profile_photo_url }));
      setPhotoPreview(null);
      toast.success('Profielfoto geüpload');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De foto kon niet worden geüpload. Controleer het bestandsformaat (max. 5MB).'));
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setSaving(true);
    try {
      await api.patch('/user/profile', { profile_photo_url: '' });
      setProfile(p => ({ ...p, profile_photo_url: '' }));
      setPhotoPreview(null);
      toast.success('Profielfoto verwijderd');
    } catch (error) {
      toast.error('Foto verwijderen mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.patch('/user/profile', { full_name: profile.full_name, job_title: profile.job_title });
      toast.success('Profiel bijgewerkt');
    } catch (error) {
      toast.error('Profiel opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await api.patch('/user/preferences', preferences);
      toast.success('Voorkeuren opgeslagen');
    } catch (error) {
      toast.error('Voorkeuren opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Onbekend';
    try {
      const date = new Date(dateString);
      return format(date, 'PPP', { locale: nl });
    } catch {
      return 'Onbekend';
    }
  };

  const formatActivityTime = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd MMM HH:mm', { locale: nl });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'login': return <User className="h-3 w-3" strokeWidth={1.5} />;
      case 'profile_update': return <Camera className="h-3 w-3" strokeWidth={1.5} />;
      case 'preferences_update': return <Bell className="h-3 w-3" strokeWidth={1.5} />;
      default: return <Activity className="h-3 w-3" strokeWidth={1.5} />;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </MainLayout>
    );
  }

  // Determine initial tab based on URL params
  const tabMap = { history: 'geschiedenis', beveiliging: 'beveiliging', profiel: 'profiel' };
  const initialTab = mfaSetupRequired ? 'beveiliging' : (tabMap[urlTab] || urlTab || 'profiel');

  return (
    <MainLayout>
      <div className="p-6 lg:p-8 space-y-8">
        {/* Header */}
        <PageHeader
          title="Instellingen"
          tooltip="Pas uw persoonlijke instellingen aan, beheer notificatievoorkeuren en configureer beveiligingsopties zoals MFA."
          testId="settings-title"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Tabbed Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue={initialTab} className="w-full">
              <TabsList className="inline-flex justify-start bg-neutral-100 p-1 rounded-lg h-auto" data-testid="settings-tabs">
                <TabsTrigger value="profiel" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="settings-tab-profiel">
                  <User className="h-3.5 w-3.5" strokeWidth={1.5} />Profiel
                </TabsTrigger>
                <TabsTrigger value="meldingen" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="settings-tab-meldingen">
                  <Bell className="h-3.5 w-3.5" strokeWidth={1.5} />Meldingen
                </TabsTrigger>
                <TabsTrigger value="beveiliging" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="settings-tab-beveiliging">
                  <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />Beveiliging
                </TabsTrigger>
              </TabsList>

              {/* PROFIEL TAB */}
              <TabsContent value="profiel" className="mt-6 space-y-6">
                <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="profile-card">
                  <CardHeader>
                    <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                      Profiel
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Avatar Section with Upload */}
                    <div className="flex items-start gap-5">
                      <div className="relative group">
                        {(photoPreview || profile.profile_photo_url) ? (
                          <img 
                            src={photoPreview || profile.profile_photo_url} 
                            alt={profile.full_name}
                            className="h-20 w-20 rounded-full object-cover border-2 border-neutral-100"
                            data-testid="profile-photo-preview"
                          />
                        ) : (
                          <div className="h-20 w-20 rounded-full bg-neutral-100 flex items-center justify-center border-2 border-neutral-200">
                            <span className="text-2xl font-semibold text-neutral-600">
                              {profile.full_name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                        )}
                        {uploadingPhoto && (
                          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                            <Loader2 className="h-5 w-5 animate-spin text-white" />
                          </div>
                        )}
                        <label
                          htmlFor="photo-upload-input"
                          className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 flex items-center justify-center cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                          data-testid="profile-photo-upload-overlay"
                        >
                          <Camera className="h-5 w-5 text-white drop-shadow" strokeWidth={1.5} />
                        </label>
                        <input
                          id="photo-upload-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                          data-testid="profile-photo-file-input"
                        />
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <h2 className="text-lg font-semibold text-black">{profile.full_name}</h2>
                        <p className="text-sm text-neutral-500">{user?.email}</p>
                        {profile.job_title && (
                          <Badge variant="outline" className="mt-1 border-neutral-200">
                            {profile.job_title}
                          </Badge>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <label
                            htmlFor="photo-upload-input"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-black cursor-pointer transition-colors px-2.5 py-1.5 rounded-md border border-neutral-200 hover:border-neutral-300 bg-white"
                            data-testid="profile-photo-upload-btn"
                          >
                            <Upload className="h-3.5 w-3.5" strokeWidth={1.5} />
                            Foto uploaden
                          </label>
                          {profile.profile_photo_url && (
                            <button
                              onClick={handleRemovePhoto}
                              className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 cursor-pointer transition-colors px-2 py-1.5 rounded-md hover:bg-red-50"
                              data-testid="profile-photo-remove-btn"
                            >
                              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                              Verwijderen
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-neutral-400">JPG, PNG of WebP. Max 2MB.</p>
                      </div>
                    </div>

                    <Separator className="bg-neutral-100" />

                    {/* Editable Fields */}
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="full_name" className="text-sm text-neutral-600">Naam</Label>
                        <Input
                          id="full_name"
                          value={profile.full_name}
                          onChange={(e) => setProfile(p => ({ ...p, full_name: e.target.value }))}
                          className="border-neutral-200"
                          data-testid="profile-name-input"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="job_title" className="text-sm text-neutral-600">Functie</Label>
                        <Input
                          id="job_title"
                          value={profile.job_title}
                          onChange={(e) => setProfile(p => ({ ...p, job_title: e.target.value }))}
                          placeholder="bijv. IT Manager, CISO"
                          className="border-neutral-200"
                          data-testid="profile-jobtitle-input"
                        />
                      </div>
                    </div>

                    <Button 
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="bg-black text-white hover:bg-neutral-800"
                      data-testid="save-profile-button"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />}
                      Profiel Opslaan
                    </Button>
                  </CardContent>
                </Card>

                {/* Uitloggen */}
                <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="danger-zone-card">
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-black">Rondleiding</p>
                        <p className="text-xs text-neutral-500">Start de introductietour opnieuw</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                        onClick={() => {
                          resetOnboardingTour();
                          resetContextualTips();
                          resetAllCoachmarks();
                          toast.success('Tour en coachmarks worden opnieuw gestart');
                          setTimeout(() => window.location.href = '/dashboard', 800);
                        }}
                        data-testid="restart-tour-button"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
                        Opnieuw starten
                      </Button>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-black">Uitloggen</p>
                        <p className="text-xs text-neutral-500">Log uit op dit apparaat</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                        onClick={handleLogout}
                        data-testid="logout-button-settings"
                      >
                        <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                        Uitloggen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* MELDINGEN TAB */}
              <TabsContent value="meldingen" className="mt-6">
                <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="notifications-card">
                  <CardHeader>
                    <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                      Meldingen & Reminders
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Bepaal welke meldingen u ontvangt en hoe vaak
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Weergave Section */}
                    <div className="space-y-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Weergave</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Info className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-black">Paginatitel-tooltips</p>
                            <p className="text-xs text-neutral-500">Toon informatieve tooltips achter paginatitels</p>
                          </div>
                        </div>
                        <Switch
                          checked={(() => { try { return localStorage.getItem('spooker_show_page_tooltips') !== 'false'; } catch { return true; } })()}
                          onCheckedChange={(checked) => { localStorage.setItem('spooker_show_page_tooltips', String(checked)); toast.success(checked ? 'Tooltips zichtbaar' : 'Tooltips verborgen'); }}
                          data-testid="page-tooltips-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Activity className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-black">Actiepunten-ticker</p>
                            <p className="text-xs text-neutral-500">Toon scrollende actiepunten in de navigatiebalk</p>
                          </div>
                        </div>
                        <Switch
                          checked={(() => { try { return localStorage.getItem('spooker_show_ticker') !== 'false'; } catch { return true; } })()}
                          onCheckedChange={(checked) => { localStorage.setItem('spooker_show_ticker', String(checked)); window.dispatchEvent(new Event('ticker-toggle')); toast.success(checked ? 'Ticker zichtbaar' : 'Ticker verborgen'); }}
                          data-testid="ticker-switch"
                        />
                      </div>
                    </div>

                    <Separator className="bg-neutral-100" />

                    {/* Notificaties Section */}
                    <div className="space-y-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Notificaties</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-black">E-mail notificaties</p>
                            <p className="text-xs text-neutral-500">Ontvang e-mails bij nieuwe risico's, voltooide scans en openstaande acties</p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences.email_notifications}
                          onCheckedChange={(checked) => setPreferences(p => ({ ...p, email_notifications: checked }))}
                          data-testid="email-notifications-switch"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Bell className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-black">In-app notificaties</p>
                            <p className="text-xs text-neutral-500">Toon meldingen in de applicatie voor statuswijzigingen en deadlines</p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences.in_app_notifications}
                          onCheckedChange={(checked) => setPreferences(p => ({ ...p, in_app_notifications: checked }))}
                          data-testid="inapp-notifications-switch"
                        />
                      </div>

                      <Separator className="bg-neutral-100" />

                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <Clock className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                          <Label className="text-sm font-medium text-black">Re-Assessment Reminders</Label>
                        </div>
                        <Select
                          value={preferences.reminder_frequency}
                          onValueChange={(value) => setPreferences(p => ({ ...p, reminder_frequency: value }))}
                        >
                          <SelectTrigger className="w-full border-neutral-200" data-testid="reminder-frequency-select">
                            <SelectValue placeholder="Selecteer frequentie" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Geen reminders</SelectItem>
                            <SelectItem value="weekly">Wekelijks</SelectItem>
                            <SelectItem value="monthly">Maandelijks</SelectItem>
                            <SelectItem value="quarterly">Per kwartaal</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-neutral-500">
                          Ontvang periodiek een herinnering om uw security status opnieuw te beoordelen en te controleren op nieuwe kwetsbaarheden
                        </p>
                      </div>

                      <Separator className="bg-neutral-100" />

                      {/* Real-time Polling Section */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <RefreshCw className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                            <div>
                              <p className="text-sm font-medium text-black">Real-time verversing</p>
                              <p className="text-xs text-neutral-500">Ververs pagina-data automatisch op de achtergrond zonder de pagina te herladen</p>
                            </div>
                          </div>
                          <Switch
                            checked={preferences.polling_enabled || false}
                            onCheckedChange={(checked) => setPreferences(p => ({ ...p, polling_enabled: checked }))}
                            data-testid="polling-enabled-switch"
                          />
                        </div>

                        {preferences.polling_enabled && (
                          <div className="ml-7 pl-3 border-l-2 border-neutral-200 space-y-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs text-neutral-600">Interval (seconden)</Label>
                              <Select
                                value={String(preferences.polling_interval || 60)}
                                onValueChange={(value) => setPreferences(p => ({ ...p, polling_interval: parseInt(value) }))}
                              >
                                <SelectTrigger className="w-full border-neutral-200 h-8 text-xs" data-testid="polling-interval-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="30">Elke 30 seconden</SelectItem>
                                  <SelectItem value="60">Elke minuut</SelectItem>
                                  <SelectItem value="120">Elke 2 minuten</SelectItem>
                                  <SelectItem value="300">Elke 5 minuten</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs text-neutral-600">Actief op pagina's</Label>
                              {[
                                { key: 'dashboard', label: 'Dashboard', desc: 'KPI\'s en overzichtsdata' },
                                { key: 'risks', label: 'Risico\'s', desc: 'Risicoregister en heatmap' },
                                { key: 'threats', label: 'Aanvallen', desc: 'Aanvalsoverzicht' },
                                { key: 'vulnerabilities', label: 'Zwakke plekken', desc: 'Scan resultaten' },
                              ].map(page => (
                                <div key={page.key} className="flex items-center justify-between py-1">
                                  <div>
                                    <p className="text-xs font-medium text-black">{page.label}</p>
                                    <p className="text-[10px] text-neutral-400">{page.desc}</p>
                                  </div>
                                  <Switch
                                    checked={preferences.polling_pages?.[page.key] || false}
                                    onCheckedChange={(checked) => setPreferences(p => ({
                                      ...p,
                                      polling_pages: { ...(p.polling_pages || {}), [page.key]: checked }
                                    }))}
                                    data-testid={`polling-page-${page.key}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <Button 
                      onClick={handleSavePreferences}
                      disabled={saving}
                      className="bg-black text-white hover:bg-neutral-800"
                      data-testid="save-preferences-button"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" strokeWidth={1.5} />}
                      Voorkeuren Opslaan
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* BEVEILIGING TAB */}
              <TabsContent value="beveiliging" className="mt-6 space-y-6">
                <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="security-card">
                  <CardHeader>
                    <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                      Beveiliging
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Beheer uw wachtwoord, tweestapsverificatie en toegangsinstellingen
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Password Change */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-black">Wachtwoord wijzigen</p>
                        <p className="text-xs text-neutral-500">Wijzig uw wachtwoord regelmatig voor extra beveiliging</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-neutral-200"
                        onClick={() => setShowPasswordDialog(true)}
                        data-testid="change-password-button"
                      >
                        Wijzigen
                      </Button>
                    </div>

                    <Separator className="bg-neutral-100" />

                    {/* MFA Required Warning */}
                    {mfaSetupRequired && (
                      <Alert className="border-red-200 bg-red-50">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <AlertTitle className="text-red-800">MFA Verplicht</AlertTitle>
                        <AlertDescription className="text-red-700">
                          Tweestapsverificatie (MFA) is verplicht voor uw account. 
                          Stel dit nu in om door te gaan met het gebruik van het platform.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* MFA Section */}
                    <MFASection api={api} forceSetup={mfaSetupRequired} />

                    <Separator className="bg-neutral-100" />

                    {/* Admin Access Section */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Building className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-black">Admin Toegang</p>
                            <p className="text-xs text-neutral-500">
                              Sta Spooker admins toe om uw organisatie data in te zien voor support-doeleinden
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {savingAdminAccess && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
                          <Switch
                            checked={allowAdminAccess}
                            onCheckedChange={handleToggleAdminAccess}
                            disabled={savingAdminAccess}
                            data-testid="admin-access-toggle"
                          />
                        </div>
                      </div>
                      {allowAdminAccess && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-xs text-amber-700">
                            Admins kunnen nu uw organisatie gegevens, abonnement, facturen en instellingen bekijken en beheren.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* UITBREIDINGEN TAB */}
            </Tabs>
          </div>

          {/* Right Column - Account Info & Activity */}
          <div className="space-y-6">
            {/* Account Info */}
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                  Account Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-neutral-500">E-mail</p>
                    <p className="text-sm font-medium text-black truncate">{user?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-neutral-500">Organisatie</p>
                    <p className="text-sm font-medium text-black">{user?.organization_name || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-neutral-500">Rol</p>
                    <p className="text-sm font-medium text-black">
                      {user?.role === 'admin' ? 'Administrator' : user?.role === 'partner' ? 'Leverancier' : 'Gebruiker'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-neutral-500">Lid sinds</p>
                    <p className="text-sm font-medium text-black">{formatDate(user?.created_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                  Recente Activiteit
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activities.length === 0 ? (
                  <p className="text-sm text-neutral-500 text-center py-4">
                    Geen activiteiten
                  </p>
                ) : (
                  <div className="space-y-3">
                    {activities.slice(0, 8).map((activity) => (
                      <div key={activity.id} className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                          {getActivityIcon(activity.action_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-neutral-700 truncate">
                            {activity.description}
                          </p>
                          <p className="text-[10px] text-neutral-400">
                            {formatActivityTime(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Version Info */}
        <div className="text-center text-xs text-neutral-400">
          <p>Spooker v1.0.0</p>
        </div>
      </div>
      
      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Wachtwoord wijzigen</DialogTitle>
            <DialogDescription>
              Voer uw huidige wachtwoord in en kies een nieuw wachtwoord.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Huidig wachtwoord</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                placeholder="Uw huidige wachtwoord"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nieuw wachtwoord</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Minimaal 8 tekens"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Bevestig nieuw wachtwoord</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Herhaal nieuw wachtwoord"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handlePasswordChange} 
              disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              data-testid="save-password-button"
            >
              {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default SettingsPage;
